import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import dayjsLocalTH from 'dayjs/locale/th';
import { chain, compact, filter, flatMap, floor, get, isArray, isEmpty, isNil, join, map, reduce, size } from 'lodash';
import { v4 as uuidV4 } from 'uuid';
import type { DurationUnitType } from 'dayjs/plugin/duration';
import type { Scope, TranslateOptions } from 'i18n-js';
import type { SyntheticEvent } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import haversine from 'haversine-distance';
import { createScopedI18n, i18n, joinScope } from '../i18n/i18n';
import { HOLIDAY_LIST, RUN_ENV, SPECIAL_HOLIDAY_LIST } from '../config';

import type { Job, JobApplicant, JobHiring } from '../graphql/graphql';
import type { Location } from '../components/google_static_maps';

export const joinPairs = (pairs?: string[][] | null) =>
  chain(pairs)
    .map((pair) => join(pair, ' '))
    .join(', ')
    .value();

export const sxMerge = (...items: Array<SxProps<Theme> | false | null | undefined>): SxProps<Theme> =>
  flatMap(items, (item) => {
    if (isNil(item) || item === false) return [];
    return Array.isArray(item) ? item : [item];
  });

export const formatDate = (
  date?: string | number | Date | dayjs.Dayjs | null | undefined,
  format: string = 'ddd D MMM YYYY',
  locale: { preset: string | ILocale; object?: Partial<ILocale> | undefined } = { preset: 'th', object: dayjsLocalTH },
) => dayjs(date).locale(locale.preset, locale.object).format(format);

export const formatTime = (
  time?: string | number | Date | dayjs.Dayjs | null | undefined,
  addMinutes: number = 0,
  format: string = 'HH:mm',
  locale: { preset: string | ILocale; object?: Partial<ILocale> | undefined } = { preset: 'th', object: dayjsLocalTH },
) => {
  let timeObj = dayjs(time);
  if (!timeObj.isValid() && typeof time === 'string') {
    timeObj = dayjs(`1970-01-01 ${time}`);
  }
  return timeObj.add(addMinutes, 'minutes').locale(locale.preset, locale.object).format(format);
};

export const formattedDuration = (
  time: number,
  unit: DurationUnitType = 'minutes',
  format: 'hourMinute' | 'hourOnly' | 'minuteOnly' = 'hourMinute',
) => {
  const durationTime = dayjs.duration(time, unit);
  const hours = floor(durationTime.asHours());
  const minutes = floor(durationTime.subtract(hours, 'hours').asMinutes());

  let hoursStr = null;
  if ((format === 'hourMinute' || format === 'hourOnly') && hours) {
    hoursStr = i18n.t('datetime.formats.duration.hours', { count: hours, joinOutput: true });
  }

  let minutesStr = null;
  if ((format === 'hourMinute' || format === 'minuteOnly') && minutes) {
    minutesStr = i18n.t('datetime.formats.duration.minutes', { count: minutes, joinOutput: true });
  }

  return join(filter([hoursStr, minutesStr]), ' ');
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Nothing {}
type Union<T, U> = T | (U & Nothing);

export type labelType = Union<
  | 'business_type'
  | 'certificate_type'
  | 'gender'
  | 'job_duty'
  | 'job_status'
  | 'job_type'
  | 'job_urgents'
  | 'pre_experience'
  | 'referrer_type',
  string
>;
export const labelForEnum = (
  labelType: labelType,
  value: string | null | undefined,
  options?: TranslateOptions | undefined,
) => {
  if (value) return i18n.t(['labels', labelType, value], options);
};

export type labelScopeType = Union<
  | 'job.duties'
  | 'job.genders'
  | 'job.job_types'
  | 'job.job_urgents'
  | 'job.statuses'
  | 'register.business_types'
  | 'register.referrer_types',
  Scope
>;
export const labelFieldEnum = (
  labelScope: labelScopeType,
  value: string | null | undefined,
  options?: TranslateOptions | undefined,
) => {
  if (value) return i18n.t(joinScope('graphql.fields', labelScope, value), options);
};

export const getDeviceUUID = () => {
  let savedDeviceUUID = localStorage.getItem('DEVICE_UUID');
  if (isNil(savedDeviceUUID)) {
    savedDeviceUUID = uuidV4();
    localStorage.setItem('DEVICE_UUID', savedDeviceUUID);
  }

  return savedDeviceUUID;
};

export const hookFormNestedFieldErrorMessage = (
  fieldError?: { message?: string } | { message?: string }[],
  options = { joinErrorMessages: true },
) => {
  if (isArray(fieldError)) {
    const compactFieldError = compact(fieldError);
    return options.joinErrorMessages ? join(map(compactFieldError, 'message'), ', ') : compactFieldError[0]?.message;
  }

  return fieldError?.message;
};

export type DayType = 'weekday' | 'weekend' | 'holiday' | 'special';

export const dateClassifier = (datetime: Date | string | Dayjs): DayType => {
  const date = dayjs(datetime);
  if (!date.isValid()) {
    return dateClassifier(dayjs());
  }

  const formattedDate = date.format('YYYY-MM-DD');

  if (SPECIAL_HOLIDAY_LIST[formattedDate]) {
    return 'special';
  }

  if (HOLIDAY_LIST[formattedDate]) {
    return 'holiday';
  }

  const dayOfWeek = date.day();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isFriday = dayOfWeek === 5;

  if (isWeekend || isFriday) {
    return 'weekend';
  }

  return 'weekday';
};

export const notificationDataToRouteMap = (
  data?: { forwardUri?: string; routeName?: string; params?: { [key: string]: string } } | null,
) => {
  const routeStatus: {
    routePath: string | null;
    isExternal: boolean;
    params?: { [key: string]: string } | undefined;
  } = { routePath: null, isExternal: false, params: data?.params };

  if (data?.forwardUri) {
    routeStatus.routePath = data.forwardUri;
    routeStatus.isExternal = true;
    return routeStatus;
  }

  switch (data?.routeName) {
    case 'CreditUsage':
      routeStatus.routePath = '/credit';
      break;

    case 'DateCandidateList': {
      const jobId = get(data.params, 'jobId');
      routeStatus.routePath = isNil(jobId) ? '/jobs' : `/jobs/${jobId}`;
      break;
    }

    case 'DateJobList': {
      const jobId = get(data.params, 'jobId');
      routeStatus.routePath = isNil(jobId) ? '/jobs' : `/jobs/${jobId}`;
      break;
    }

    case 'JobList': {
      const jobId = get(data.params, 'jobId');
      routeStatus.routePath = isNil(jobId) ? '/jobs' : `/jobs/${jobId}`;
      break;
    }

    default:
      routeStatus.routePath = null;
      break;
  }

  return routeStatus;
};

export const toLatLng = (
  location?: {
    lat?: number | google.maps.LatLng['lat'] | null;
    latitude?: number | null;
    lng?: number | google.maps.LatLng['lng'] | null;
    longitude?: number | null;
  } | null,
): Location | undefined => {
  const lat = (typeof location?.lat === 'function' ? location.lat() : location?.lat) ?? location?.latitude;
  const lng = (typeof location?.lng === 'function' ? location.lng() : location?.lng) ?? location?.longitude;
  if (lat && lng) {
    return { lat, lng };
  }
};

export const errorsPairI18n = (
  scopedI18n: ReturnType<typeof createScopedI18n>,
  errorPairs: string[][] | null,
  separator = ', ',
  i18nOptions?: TranslateOptions | undefined,
) => {
  const errorMessage = join(
    compact(
      map(errorPairs, (errors) =>
        isEmpty(errors) ? null : scopedI18n(errors, { defaultValue: join(errors, separator), ...i18nOptions }),
      ),
    ),
    separator,
  );

  return errorMessage;
};

export const isOpenJobApplicant = (jobApplicant: JobApplicant) => jobApplicant.status === 'open';

export const isMatchOrDoneHiring = (jobHiring: JobHiring) => jobHiring.status === 'match' || 'done';

export const filterOpenJobApplicants = (jobApplicants: JobApplicant[]) => filter(jobApplicants, isOpenJobApplicant);

export const filterMatchOrDoneHirings = (jobHirings: JobHiring[]) => filter(jobHirings, isMatchOrDoneHiring);

export const calJobsPositionCountMap = (jobs: Job[]) =>
  reduce(
    jobs,
    (prev, job) => {
      const hirings = filterMatchOrDoneHirings(job.jobHirings ?? []);
      const applicants = filterOpenJobApplicants(job.jobApplicants ?? []);
      return { ...prev, [job.id]: { applicantCount: size(applicants), hiringCount: size(hirings) } };
    },
    {} as { [key: string]: { applicantCount: number; hiringCount: number } },
  );

export const stopEventPropagation = (e: SyntheticEvent) => e.stopPropagation();

export const calculateDurationMinute = (
  startTime: Date | string | Dayjs | null | undefined,
  endTime: Date | string | Dayjs | null | undefined,
  breakDuration: number | null | undefined = 0,
  float = false,
) => {
  if (isNil(startTime) || isNil(endTime)) {
    return null;
  }

  const start = dayjs(startTime);
  const end = dayjs(endTime);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  const duration = end.diff(start, 'minute', float) - (breakDuration ?? 0);

  return duration;
};

export const mergeTimeWithDate = (
  time: Dayjs,
  date: Dayjs,
  maxPrecision: 'hour' | 'minute' | 'second' | 'millisecond' = 'second',
) => {
  let mergedDate = date.hour(time.hour()).minute(time.minute()).second(time.second()).millisecond(time.millisecond());
  switch (maxPrecision) {
    case 'hour':
      mergedDate = mergedDate.minute(0).second(0).millisecond(0);
      break;
    case 'minute':
      mergedDate = mergedDate.second(0).millisecond(0);
      break;
    case 'second':
      mergedDate = mergedDate.millisecond(0);
      break;
    case 'millisecond':
      break;
    default:
      break;
  }

  return mergedDate;
};

/**
 * Merges picked time with a target date.
 * If isEnd is true and the resulting time is before the start time,
 * it adds 1 day to handle overnight shifts.
 */
export const syncTimeToDate = (
  time: Dayjs,
  targetDate: string | Dayjs | Date | null | undefined,
  startTime?: string | Dayjs | Date | null,
) => {
  if (isNil(targetDate) && RUN_ENV !== 'production') {
    console.warn('targetDate is missing, Date merging may not work as expected');
  }

  let merged = mergeTimeWithDate(time, dayjs(targetDate ?? undefined));

  if (!isNil(startTime)) {
    const startObj = dayjs(startTime);
    if (merged.isBefore(startObj)) {
      merged = merged.add(1, 'day');
    }
  }

  return merged;
};

export type Coordinates =
  | { latitude: number; longitude: number }
  | { lat: number; lng: number }
  | { lat: number; lon: number }
  | [number, number];

export const haversineDistance = (location: Coordinates | null, otherLocation: Coordinates | null) => {
  if (isNil(location) || isNil(otherLocation)) return null;
  return haversine(location, otherLocation);
};

export const distanceInRange = (distance: number | null, range: number) => (isNil(distance) ? true : distance <= range);

export const locationInRange = (location: Coordinates | null, otherLocation: Coordinates | null, range: number) => {
  const distance = haversineDistance(location, otherLocation);
  return distanceInRange(distance, range);
};
